/**
 * Shared provider-first grouping helpers for Source[].
 *
 * Provider-first model (see CLAUDE.md memory):
 *   - Primary key: matched_provider_id (FK to known_providers)
 *   - Fallback: provider_name string
 *   - Final fallback: org_name
 *   - Sources with none of the above are "unknown"
 */

import type { Source } from "@/lib/api";

/** Canonical group key for a source, or null if the source is unknown. */
export function getProviderKey(source: Source): string | null {
  return source.matched_provider_id || source.provider_name || source.org_name || null;
}

/** Human-friendly display name for a provider group. */
export function getProviderDisplayName(source: Source): string {
  return (
    source.known_provider_name ||
    source.provider_name ||
    source.org_name ||
    getProviderKey(source) ||
    "Unknown"
  );
}

export interface ProviderGroup {
  key: string;
  providerId: string | null;
  providerName: string;
  providerCategory: string | null;
  sources: Source[];
  totalVolume: number;
  weightedSpfRate: number;
  weightedDkimRate: number;
  effectiveStatus: "authorized" | "pending_review" | "unauthorized";
  ipCount: number;
}

/**
 * Group sources into provider groups with aggregate stats plus a bucket for
 * unknown (unmatched, no provider_name, no org_name) sources.
 *
 * @param sources Sources to group
 * @param isForwarder Predicate used to switch DKIM rate source (auth vs alignment)
 *   for forwarder sources when computing the volume-weighted DKIM rate.
 */
export function groupSourcesByProvider(
  sources: Source[],
  isForwarder: (s: Source) => boolean = () => false,
): { providers: ProviderGroup[]; unknownSources: Source[] } {
  const groups = new Map<string, ProviderGroup>();
  const unknownSources: Source[] = [];

  for (const source of sources) {
    const key = getProviderKey(source);
    if (!key) {
      unknownSources.push(source);
      continue;
    }

    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        providerId: source.matched_provider_id || null,
        providerName: getProviderDisplayName(source),
        providerCategory: source.known_provider_category || null,
        sources: [],
        totalVolume: 0,
        weightedSpfRate: 0,
        weightedDkimRate: 0,
        effectiveStatus: "authorized",
        ipCount: 0,
      };
      groups.set(key, group);
    }
    group.sources.push(source);
  }

  for (const group of groups.values()) {
    group.ipCount = group.sources.length;
    group.totalVolume = group.sources.reduce((sum, s) => sum + s.total_message_count, 0);

    if (group.totalVolume > 0) {
      group.weightedSpfRate =
        group.sources.reduce(
          (sum, s) => sum + (s.spf_pass_rate || 0) * s.total_message_count,
          0,
        ) / group.totalVolume;

      group.weightedDkimRate =
        group.sources.reduce((sum, s) => {
          const rate =
            isForwarder(s) && s.dkim_auth_pass_rate != null
              ? s.dkim_auth_pass_rate
              : s.dkim_pass_rate || 0;
          return sum + rate * s.total_message_count;
        }, 0) / group.totalVolume;
    }

    const hasUnauthorized = group.sources.some((s) => s.authorization_status === "unauthorized");
    const hasPending = group.sources.some(
      (s) => s.authorization_status === "pending_review" || !s.authorization_status,
    );
    group.effectiveStatus = hasUnauthorized
      ? "unauthorized"
      : hasPending
        ? "pending_review"
        : "authorized";
  }

  // Sort: matched providers first (alpha), then string-only groups, then alpha
  const providers = Array.from(groups.values()).sort((a, b) => {
    if (a.providerId && !b.providerId) return -1;
    if (!a.providerId && b.providerId) return 1;
    return a.providerName.localeCompare(b.providerName);
  });

  return { providers, unknownSources };
}

/**
 * Lightweight grouping: returns a Map keyed by provider display name with
 * sources as values. Unknown sources bucket under "Unknown". Used by the
 * APEX wizard where aggregate stats aren't needed.
 */
export function groupSourcesByProviderName(sources: Source[]): Map<string, Source[]> {
  const groups = new Map<string, Source[]>();
  for (const source of sources) {
    const key = getProviderDisplayName(source);
    const bucket = groups.get(key);
    if (bucket) {
      bucket.push(source);
    } else {
      groups.set(key, [source]);
    }
  }
  return new Map(
    [...groups.entries()].sort((a, b) => {
      if (a[0] === "Unknown") return 1;
      if (b[0] === "Unknown") return -1;
      return a[0].localeCompare(b[0]);
    }),
  );
}
